import { useMemo, useState, useEffect } from 'react';
import { useUrl } from 'crossroad';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
    Lightbulb,
    ChevronDown,
    Clock,
    Smartphone,
    Sparkles,
    UserCircle2,
    Check
} from 'lucide-react';
import engagementMethodsData from '@/data/scripture-engagement-methods.json';
import personasData from '@/data/personas.json';
import { useStudyGuideByPassageData } from '@/data/study-guide/study-guide-by-passage-data-context';
import { DiscoveryCard } from './discovery-card';
import { cn } from '@/lib/utils';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

// Hero illustration
// Hero illustration
import adventureHero from '@/assets/heroes/sacred_rhythms_hero_v1.png';
import { ContextHero } from '../context/ContextHero';

interface Props {
    passageId: string;
}

type SocialContext = 'alone' | 'pair' | 'small-group' | 'family' | 'large-group';
type Difficulty = 'beginner' | 'intermediate' | 'advanced';
type AppSupport = 'full' | 'partial' | 'none';
type Intent = 'analytical' | 'reflective' | 'active' | 'expressive' | 'imaginative';

interface MethodStep {
    id: string;
    title: string;
    description: string;
    capabilityRef?: string;
}

interface EngagementMethod {
    id: string;
    title: string;
    summary: string;
    description: string;
    socialContext: SocialContext[];
    difficulty: Difficulty;
    timeEstimate: string;
    minMinutes: number;
    maxMinutes: number;
    materialsNeeded: string[];
    searchQuery: string;
    targetPersonas: string[];
    appSupport: AppSupport;
    relatedCapabilities: string[];
    suitableGenres: string[];
    intent: Intent[];
    steps?: MethodStep[];
}

interface Category {
    id: string;
    title: string;
    methods: EngagementMethod[];
}

const SOCIAL_CONTEXT_LABELS: Record<SocialContext, string> = {
    'alone': 'Alone',
    'pair': 'With a Partner',
    'small-group': 'Small Group',
    'family': 'Family',
    'large-group': 'Large Group'
};

const getDifficultyColors = (difficulty: Difficulty, isDarkMode: boolean): string => {
    const colors = {
        'beginner': isDarkMode ? 'text-green-400 bg-green-500/10 border-green-500/20' : 'text-green-600 bg-green-500/10 border-green-500/20',
        'intermediate': isDarkMode ? 'text-amber-400 bg-amber-500/10 border-amber-500/20' : 'text-amber-600 bg-amber-500/10 border-amber-500/20',
        'advanced': isDarkMode ? 'text-rose-400 bg-rose-500/10 border-rose-500/20' : 'text-rose-600 bg-rose-500/10 border-rose-500/20'
    };
    return colors[difficulty];
};

const APP_SUPPORT_LABELS: Record<AppSupport, string> = {
    'full': 'Full Digital Integration',
    'partial': 'Guided Experience',
    'none': 'Manual Method'
};

const INTENT_LABELS: Record<Intent, string> = {
    'analytical': 'Analytical',
    'reflective': 'Reflective',
    'active': 'Active',
    'expressive': 'Expressive',
    'imaginative': 'Imaginative'
};

export default function PassageEngagementMethods({ passageId }: Props) {
    const isDarkMode = useDarkMode();
    const [, setUrl] = useUrl();
    const { studyGuideByPassageMap } = useStudyGuideByPassageData();
    const studyGuide = studyGuideByPassageMap.get(passageId);

    const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
    const [selectedSocialContexts, setSelectedSocialContexts] = useState<Set<SocialContext>>(new Set());
    const [showPassageSpecificOnly, setShowPassageSpecificOnly] = useState(!!studyGuide);
    const [expandedMethods, setExpandedMethods] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (studyGuide && !showPassageSpecificOnly) {
            setShowPassageSpecificOnly(true);
        }
    }, [studyGuide]);

    const categories = engagementMethodsData.categories as Category[];
    const personas = personasData.personas;
    const selectedPersona = personas.find(p => p.id === selectedPersonaId);

    const filteredCategories = useMemo(() => {
        const recommendedMethodIds = studyGuide
            ? new Set(studyGuide.methodRelevance.map(m => m.methodId))
            : new Set<string>();

        return categories.map(category => {
            const filteredMethods = category.methods.filter(method => {
                if (showPassageSpecificOnly && studyGuide) {
                    if (!recommendedMethodIds.has(method.id)) return false;
                }

                if (selectedSocialContexts.size > 0) {
                    const hasMatchingSocialContext = method.socialContext.some(sc =>
                        selectedSocialContexts.has(sc)
                    );
                    if (!hasMatchingSocialContext) return false;
                }

                if (selectedPersonaId) {
                    if (!method.targetPersonas.includes(selectedPersonaId)) return false;
                }

                return true;
            });

            return {
                ...category,
                methods: filteredMethods
            };
        }).filter(category => category.methods.length > 0);
    }, [categories, selectedSocialContexts, selectedPersonaId, showPassageSpecificOnly, studyGuide]);

    const toggleSocialContext = (context: SocialContext) => {
        const newSet = new Set(selectedSocialContexts);
        if (newSet.has(context)) {
            newSet.delete(context);
        } else {
            newSet.add(context);
        }
        setSelectedSocialContexts(newSet);
    };

    const toggleMethod = (methodId: string) => {
        const newSet = new Set(expandedMethods);
        if (newSet.has(methodId)) {
            newSet.delete(methodId);
        } else {
            newSet.add(methodId);
        }
        setExpandedMethods(newSet);
    };

    const getMethodRationale = (methodId: string) => {
        return studyGuide?.methodRelevance.find(m => m.methodId === methodId)?.rationale;
    };

    const getStepGuidance = (methodId: string, stepId: string) => {
        const methodRel = studyGuide?.methodRelevance.find(m => m.methodId === methodId);
        return methodRel?.stepRelevance?.find(s => s.stepId === stepId)?.guidance;
    };

    return (
        <div className="relative space-y-6 flex flex-col min-h-full">
            {/* Cinematic Hero */}
            <ContextHero
                eyebrow="IDEAS FOR ENGAGEMENT"
                headline="Sacred Rhythms"
                subtitle="Ancient and modern practices to bridge the gap between Biblical study and personal transformation."
                image={adventureHero}
                onClose={() => setUrl(`/app/passage/${passageId}`)}
                showClose={false}
                showHandle={false}
            />

            {/* Persona and Context Control Bar - Non-sticky per user request */}
            <div className="flex flex-col sm:flex-row items-center gap-4 bg-background/80 backdrop-blur-md p-4 rounded-xl border border-border/50 shadow-sm mx-4">
                <div className="flex-1 flex flex-wrap items-center gap-4 w-full">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="h-10 px-4 flex items-center gap-3 bg-card border-primary/20 hover:border-primary/40 transition-all rounded-lg w-full sm:w-auto">
                                <UserCircle2 className="h-4 w-4 text-primary/60" />
                                <span className="text-sm font-medium">
                                    {selectedPersona ? selectedPersona.title : "Method Preference"}
                                </span>
                                <ChevronDown className="h-4 w-4 opacity-50" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-[280px] p-2 rounded-xl">
                            <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/50 border-b border-border/40 mb-1">
                                Choose Your Style
                            </div>
                            <DropdownMenuItem
                                className="rounded-lg mb-1 flex items-center justify-between"
                                onClick={() => setSelectedPersonaId(null)}
                            >
                                <span>General / All Methods</span>
                                {!selectedPersonaId && <Check className="h-4 w-4 text-primary" />}
                            </DropdownMenuItem>
                            {personas.map(persona => (
                                <DropdownMenuItem
                                    key={persona.id}
                                    className="rounded-lg mb-1 flex flex-col items-start gap-1 cursor-pointer"
                                    onClick={() => setSelectedPersonaId(persona.id)}
                                >
                                    <div className="flex items-center justify-between w-full">
                                        <span className="font-medium text-sm">{persona.title}</span>
                                        {selectedPersonaId === persona.id && <Check className="h-4 w-4 text-primary" />}
                                    </div>
                                    <p className="text-[10px] text-muted-foreground line-clamp-1 italic">{persona.description}</p>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <div className="hidden sm:flex h-6 w-px bg-border/40 mx-2" />

                    <div className="flex flex-wrap gap-2 items-center">
                        {(Object.keys(SOCIAL_CONTEXT_LABELS) as SocialContext[]).slice(0, 3).map(ctx => (
                            <Badge
                                key={ctx}
                                variant={selectedSocialContexts.has(ctx) ? "default" : "outline"}
                                className={cn(
                                    "cursor-pointer px-3 py-1 text-[10px] transition-all duration-300",
                                    !selectedSocialContexts.has(ctx) && "bg-transparent border-border/40 hover:border-primary/40"
                                )}
                                onClick={() => toggleSocialContext(ctx)}
                            >
                                {SOCIAL_CONTEXT_LABELS[ctx]}
                            </Badge>
                        ))}
                    </div>
                </div>

                {studyGuide && (
                    <div className="flex items-center gap-3 bg-primary/5 px-4 py-2 rounded-lg border border-primary/10">
                        <Sparkles className="h-3.5 w-3.5 text-primary/60" />
                        <Label htmlFor="passage-specific" className="text-[11px] font-bold uppercase tracking-wider text-primary/60 cursor-pointer">
                            This Passage Only
                        </Label>
                        <Checkbox
                            id="passage-specific"
                            checked={showPassageSpecificOnly}
                            onCheckedChange={(checked) => setShowPassageSpecificOnly(!!checked)}
                            className="h-4 w-4 border-primary/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                    </div>
                )}
            </div>

            {/* Methods by Category */}
            <div className="space-y-16 px-4">
                {filteredCategories.map(category => (
                    <div key={category.id} className="space-y-6 relative">
                        {/* Section Header */}
                        <div className="space-y-1">
                            <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                {category.title}
                            </h2>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                    {category.methods.length} Method{category.methods.length !== 1 ? 's' : ''}
                                </span>
                                <div className="h-px w-8 bg-primary/20" />
                            </div>
                        </div>

                        {/* Individual Method Cards */}
                        <div className="space-y-4">
                            {category.methods.map(method => {
                                const isMethodExpanded = expandedMethods.has(method.id);
                                const rationale = getMethodRationale(method.id);

                                // Build card header
                                const methodHeader = (
                                    <div className="flex flex-col gap-3 w-full">
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            {rationale && (
                                                <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-none px-2 py-0.5">
                                                    <Sparkles className="h-3 w-3 mr-1" />
                                                    <span className="text-[9px] font-bold uppercase tracking-wider">Recommended</span>
                                                </Badge>
                                            )}
                                        </div>

                                        <div className="space-y-2">
                                            <h4 className="text-xl font-serif font-medium text-foreground/90">
                                                {method.title}
                                            </h4>
                                            <p className="text-sm text-muted-foreground font-light leading-relaxed">
                                                {method.summary}
                                            </p>
                                        </div>

                                        {rationale && (
                                            <div className="p-4 rounded-xl bg-primary/5 border border-primary/10 space-y-2">
                                                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">
                                                    <Sparkles className="h-3 w-3" />
                                                    Why this is relevant to this Passage:
                                                </div>
                                                <p className="text-sm italic font-serif leading-relaxed text-foreground/80">
                                                    {rationale}
                                                </p>
                                            </div>
                                        )}

                                        <div className="flex flex-wrap gap-2">
                                            {method.intent.map(intent => (
                                                <span key={intent} className="text-[10px] text-primary/60 font-medium px-2 py-0.5 rounded-full bg-primary/5 uppercase tracking-wider border border-primary/10">
                                                    {intent}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                );

                                return (
                                    <DiscoveryCard
                                        key={method.id}
                                        isExpanded={isMethodExpanded}
                                        onToggle={() => toggleMethod(method.id)}
                                        header={methodHeader}
                                    >
                                        <div className="space-y-6">
                                            {method.steps && <div className="space-y-3">
                                                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/50">Engagement Steps</div>
                                                <div className="space-y-6 pl-2">
                                                    {method.steps.map((step, idx) => {
                                                        const guidance = getStepGuidance(method.id, step.id);
                                                        return (
                                                            <div key={step.id} className="relative pl-6 border-l border-border/40 pb-2 last:pb-0">
                                                                <div className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-primary/40 ring-4 ring-background" />
                                                                <h5 className="text-sm font-serif font-medium text-foreground/90 flex items-center gap-3">
                                                                    <span className="text-xs text-primary/40 font-mono">0{idx + 1}</span>
                                                                    {step.title}
                                                                </h5>
                                                                <p className="mt-2 text-sm font-light text-muted-foreground leading-relaxed">
                                                                    {step.description}
                                                                </p>
                                                                {guidance && (
                                                                    <div className={cn("mt-3 p-3 rounded-lg bg-orange-500/[0.03] border border-orange-500/10 text-xs font-light flex items-start gap-3", isDarkMode ? "text-orange-300" : "text-orange-700")}>
                                                                        <Lightbulb className="h-3.5 w-3.5 shrink-0 mt-0.5 opacity-60" />
                                                                        <span><strong className="font-bold opacity-80 uppercase tracking-tighter mr-1">Pro Tip:</strong> {guidance}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>}

                                            {method.materialsNeeded?.length > 0 && (
                                                <div className="space-y-2">
                                                    <div className="text-[10px] uppercase tracking-[0.2em] ">Materials Needed</div>
                                                    <div className="flex flex-wrap gap-2">
                                                        {method.materialsNeeded.map((material, idx) => (
                                                            <Badge key={idx} variant="outline" className="text-[10px] font-medium bg-muted/30">
                                                                {material}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="pt-3 border-t border-border/10 grid grid-cols-2 gap-4 text-xs">
                                                <div>
                                                    <div className="text-[9px] uppercase tracking-widest mb-1">Social Context</div>
                                                    <div className="flex flex-wrap gap-1">
                                                        {method.socialContext.map(sc => (
                                                            <Badge key={sc} variant="secondary" className="text-[9px] font-medium">
                                                                {SOCIAL_CONTEXT_LABELS[sc]}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-[9px] uppercase tracking-widest mb-1">App Support</div>
                                                    <Badge variant="outline" className="text-[9px] font-medium">
                                                        <Smartphone className="h-2.5 w-2.5 mr-1" />
                                                        {APP_SUPPORT_LABELS[method.appSupport]}
                                                    </Badge>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className={cn("text-[9px] uppercase tracking-widest font-bold", getDifficultyColors(method.difficulty, isDarkMode))}>
                                                    {method.difficulty}
                                                </Badge>
                                                <div className="h-1 w-1 rounded-full bg-border/40" />
                                                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                                                    <Clock className="h-3 w-3" />
                                                    {method.timeEstimate}
                                                </div>
                                            </div>

                                        </div>
                                    </DiscoveryCard>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="py-8 border-t border-border/50 flex flex-col items-center gap-4">
                <p className="text-[11px] font-serif italic text-muted-foreground/40 text-center max-w-[500px] leading-relaxed px-6">
                    Scripture engagement recommendations are manually curated and algorithmically cross-referenced against your chosen persona and the detected literary structures of this specific passage.
                </p>
            </div>
        </div >
    );
}

